import { useEffect, useMemo, useState } from 'react';

import { useAuthentication } from '@givebacks/api';
import {
  ICredential,
  TokenExCardTypes,
  useListPaymentMethods,
  useListPaymentMethodsQuery,
} from '@givebacks/api/core';
import { useQuery } from '@tanstack/react-query';
import { Box, LoadingOverlay, SegmentedControl, Transition } from 'ui';

import { StripeCardInfoForm } from '../../molecules/forms/stripe/StripeCardInfoForm';
import { ExistingCardGroup } from '../ExistingCreditCard/ExistingCreditCard';

type OnCredentialChange = (credential?: ICredential | TokenExCardTypes) => void;

type ExistingCardsProps = {
  setIsMounted: React.Dispatch<React.SetStateAction<boolean>>;
};
const ExistingCards = ({ setIsMounted }: ExistingCardsProps) => {
  const { data: credentials } = useListPaymentMethods();

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  return <ExistingCardGroup credentials={credentials ?? []} />;
};

type CardSelectionProps = {
  hasExistingCredentials: boolean;
  withSaveCardCheckbox?: boolean;
};

const CardSelection = ({
  hasExistingCredentials,
  withSaveCardCheckbox = true,
}: CardSelectionProps) => {
  const [isExistingCredentialMounted, setIExistingCredentialMounted] = useState(false);

  const [controlValue, setControlValue] = useState<'existing_card' | 'new_card' | undefined>(
    hasExistingCredentials ? 'existing_card' : 'new_card'
  );

  return (
    <>
      {hasExistingCredentials && (
        <SegmentedControl
          data={[
            { label: 'Card on File', value: 'existing_card' },
            { label: 'New Card', value: 'new_card' },
          ]}
          value={controlValue}
          onChange={(value) => setControlValue(value as 'existing_card' | 'new_card')}
          fullWidth
          variant="light"
        />
      )}

      <Transition
        mounted={controlValue === 'existing_card'}
        transition="fade"
        duration={150}
        timingFunction="ease"
      >
        {(style) => (
          <Box style={style}>
            <ExistingCards setIsMounted={setIExistingCredentialMounted} />
          </Box>
        )}
      </Transition>

      <Transition
        mounted={controlValue === 'new_card' && isExistingCredentialMounted == false}
        transition="fade"
        duration={150}
        timingFunction="ease"
      >
        {(style) => (
          <Box style={style}>
            <StripeCardInfoForm title="New Card" withSaveCardCheckbox={withSaveCardCheckbox} />
          </Box>
        )}
      </Transition>
    </>
  );
};
type CheckoutCardSelectionProps = {
  withSaveCardCheckbox?: boolean;
  withCardOnFileEnabled?: boolean;
};
const CheckoutCardProvider = ({
  withSaveCardCheckbox = true,
  withCardOnFileEnabled = true,
}: CheckoutCardSelectionProps) => {
  const { isAuthenticated } = useAuthentication();

  const {
    isLoading,
    data: credentials,
    isPlaceholderData,
    isEnabled,
  } = useQuery({
    ...useListPaymentMethodsQuery(),
    enabled: withCardOnFileEnabled && isAuthenticated,
  });

  /**
   * If the payment methods are not enabled, we don't show the loading overlay
   * If the payment methods are loading, we show the loading overlay
   * If the payment methods are not loaded( we have placeholder data) and the payment methods are enabled, we show the loading overlay
   *  otherwise we don't show the loading overlay
   */
  const shouldShowLoadingOverlay = useMemo(() => {
    if (!isEnabled) return false;
    if (isLoading) return true;
    if (isPlaceholderData && isEnabled) return true;

    return false;
  }, [isLoading, isPlaceholderData, isEnabled]);

  if (shouldShowLoadingOverlay) {
    return (
      <Box h={400}>
        <LoadingOverlay visible={true} />
      </Box>
    );
  }

  return (
    <CardSelection
      // Evaluate if their are  existing credentials only when the card on file is enabled
      hasExistingCredentials={withCardOnFileEnabled ? Boolean(credentials?.length) : false}
      withSaveCardCheckbox={withSaveCardCheckbox}
    />
  );
};

export { CheckoutCardProvider };
export type { OnCredentialChange };
